import { useState, type FormEvent } from 'react';
import { CharMeter } from '../../components/CharMeter';
import { DraftNotice } from '../../components/DraftNotice';
import { Modal } from '../../components/Modal';
import { planErrorMessage } from '../../components/plan/planErrors';
import { TicketHead } from '../../components/TicketHead';
import { clearDraft, knowledgeBaseDraftKey, restoreDraft } from '../../lib/draftStorage';
import { useDraft } from '../../lib/useDraft';
import type { BrandVoiceExampleCreate, BrandVoiceExampleRead } from '../../types/api';
import { brandVoiceExamplesApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';
import { useBrandCopy } from '../onboarding/useBrandCopy';

/** Autosaved shape. Field names are the form's, not the API's. */
interface VoiceDraft {
  label: string;
  content: string;
}

function voiceDraftKey(businessId: string, item: BrandVoiceExampleRead | null): string {
  return knowledgeBaseDraftKey('voice', businessId, item?.id ?? null);
}

interface VoiceFormProps {
  businessId: string;
  initial: BrandVoiceExampleRead | null;
  isSaving: boolean;
  error: string | null;
  onSubmit: (body: BrandVoiceExampleCreate) => void;
  onCancel: () => void;
}

function VoiceExampleForm({
  businessId,
  initial,
  isSaving,
  error,
  onSubmit,
  onCancel,
}: VoiceFormProps) {
  const draftKey = voiceDraftKey(businessId, initial);
  const pristine: VoiceDraft = { label: initial?.label ?? '', content: initial?.content ?? '' };
  // The form mounts when the modal opens, so this initialiser is the "on open"
  // hook — no effect, no second render.
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [label, setLabel] = useState(restored.value.label);
  const [content, setContent] = useState(restored.value.content);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useDraft(draftKey, { label, content }, pristine);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!content.trim()) {
      setFieldError('Paste the post before saving.');
      return;
    }
    setFieldError(null);
    onSubmit({ label: label.trim() || null, content: content.trim() });
  }

  /**
   * Cancel is a decision to throw the work away. Escape and the header Close
   * are not — those leave the draft where it is.
   */
  function handleCancel() {
    clearDraft(draftKey);
    onCancel();
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {restoredAt ? (
        <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
      ) : null}

      <p className="kb-form__hint">Paste three or four posts you were happy with.</p>

      {/* The meter sits outside the label so it never joins the field's
          accessible name, which would then change on every keystroke. */}
      <div className="field-stack">
        <label className="field">
          <span className="field__label">The post, word for word</span>
          <textarea
            className="field__input"
            rows={4}
            value={content}
            placeholder="Paste a post that already sounds like you."
            onChange={(event) => setContent(event.target.value)}
          />
        </label>
        {/* These examples are real captions, so the platform limits apply. */}
        <CharMeter text={content} />
      </div>

      <label className="field">
        <span className="field__label">Where and when it ran</span>
        <input
          className="field__input"
          type="text"
          maxLength={120}
          placeholder="e.g. Instagram, last spring"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <span className="field__hint">Optional. Helps you find it later.</span>
      </label>

      {(fieldError ?? error) ? (
        <p className="form__error" role="alert">
          {fieldError ?? error}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </button>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : initial === null ? 'Add post' : 'Save post'}
        </button>
      </div>
    </form>
  );
}

/**
 * Brand voice examples — real copy the business already stands behind. The
 * generation node uses them as tone anchors.
 */
export function BrandVoiceSection({ businessId }: { businessId: string }) {
  const collection = useCollection(brandVoiceExamplesApi, businessId, 'voice_examples');
  const { capacity, isFull, planName, fullTitle, refreshCapacity } = useSectionCapacity(
    businessId,
    'example_posts',
  );
  const copy = useBrandCopy();
  const [editing, setEditing] = useState<BrandVoiceExampleRead | null | 'new'>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  function startCreate() {
    setWriteError(null);
    setEditing('new');
  }

  async function handleSubmit(body: BrandVoiceExampleCreate) {
    const draftKey = voiceDraftKey(businessId, editing === 'new' ? null : editing);
    setIsSaving(true);
    setWriteError(null);
    try {
      if (editing && editing !== 'new') {
        await collection.update(editing.id, body);
      } else {
        await collection.create(body);
        refreshCapacity();
      }
      // The record is on file; the local copy has nothing left to protect.
      clearDraft(draftKey);
      setEditing(null);
    } catch (caught) {
      // A 402 at the cap: the counts were stale, so re-read them too.
      refreshCapacity();
      setWriteError(
        planErrorMessage(caught, 'That example did not save.', {
          plan: planName,
          subject: copy.sections.voice,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the row goes now, the DELETE waits out the undo window. */
  function startDelete(item: BrandVoiceExampleRead) {
    collection.removeWithUndo({
      id: item.id,
      label: item.label ?? 'Unlabelled example',
      failureMessage: 'That example could not be deleted.',
      onRemoved: refreshCapacity,
    });
  }

  return (
    <>
      <SectionShell
        title={copy.sections.voice}
        lede={copy.records.voice.lede}
        action={
          <button
            className="button button--primary"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.voice.add}
          </button>
        }
        capacity={capacity}
        isLoading={collection.isLoading}
        error={collection.error ?? collection.deleteError}
        isEmpty={collection.items.length === 0}
        emptyMessage={copy.records.voice.empty}
        emptyAction={
          <button
            className="button"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.voice.add}
          </button>
        }
      >
        <ul className="record-list">
          {collection.items.map((item) => (
            <li className="record" key={item.id}>
              <TicketHead label="Example post" timestamp={item.created_at} />
              <div className="record__head">
                <span className="record__title">{item.label ?? 'Unlabelled example'}</span>
              </div>
              <p className="record__body">{item.content}</p>
              <div className="record__actions">
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => {
                    setWriteError(null);
                    setEditing(item);
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => startDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      {editing ? (
        <Modal
          title={editing === 'new' ? 'New example post' : 'Edit example post'}
          kind={editing === 'new' ? 'new example post' : 'example post'}
          onDismiss={() => setEditing(null)}
        >
          <VoiceExampleForm
            businessId={businessId}
            initial={editing === 'new' ? null : editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      ) : null}
    </>
  );
}
